import type { UseFormReturnType } from '@mantine/form'
import type { AppAbility, ListParams, RefMap, ResolvedField, Resource, Tone } from '@tnn/core'
import type { RecordCardProps } from '@tnn/ui'
import type { TFunction } from 'i18next'
import type { ReactNode } from 'react'
import type { Rec } from '../api/resources'
import type { Me } from '../auth/session'

export interface ListChip {
  value: string
  label: string
  /** Params applied when the chip is selected (on top of the base list params) */
  params: Partial<ListParams>
  count?: number
  tone?: Tone
}

export interface ResourceListOptions {
  /** Field shown as filter chips with counts; defaults to `status` when it has tones */
  chipsField?: string | false
  /** Extra chips after the field chips, e.g. "late": their `label` is a translation key */
  extraChips?: ListChip[]
  /** Date field with a quick day picker in the toolbar (today, last 7 days, between two days, all) */
  dateFilter?: string
  /** Columns to show instead of `resource.columns` */
  columns?: string[]
  /**
   * Fields a column's custom cell also shows, e.g. `{ origin: ['destination'] }` for a route column.
   * They get no column of their own, sort after the column's field, and print and export next to it.
   */
  combine?: Record<string, string[]>
  /** Custom cell rendering per field */
  render?: Record<string, (record: Rec, refs: RefMap) => ReactNode>
  /** Column header i18n keys that differ from the field label (e.g. a combined "route" column) */
  columnTitles?: Record<string, string>
  /** Phone card for a record; defaults to the resource's `card` layout */
  card?: (record: Rec, refs: RefMap) => RecordCardProps
  defaultParams?: Partial<ListParams>
  /**
   * Show a create button in the page header, beside the app's own: off only for records made elsewhere (a credit note
   * from its invoice, a ledger the server writes)
   */
  createInHeader?: boolean
  /** Extra buttons in the page header */
  actions?: ReactNode
  /** Extra buttons while the list has nothing yet, after create and import (e.g. HR's starter set) */
  emptyActions?: ReactNode
  /** Where a row leads; defaults to `<path>/<id>`. `false` for read-only logs */
  rowHref?: ((record: Rec) => string) | false
  /** Buttons for the selected rows, next to export and delete (e.g. take payment for several waybills) */
  bulkActions?: (selected: Rec[], clearSelection: () => void) => ReactNode
}

export interface CustomFieldProps {
  // biome-ignore lint/suspicious/noExplicitAny: form values are resource-shaped
  form: UseFormReturnType<any>
  /** The field the input is registered on (label, required…) */
  field: ResolvedField
  /** The saved record; undefined while creating */
  record?: Rec
  /** Titles of the records it points at (`refs['admin.user'][id]`), as loaded with it */
  refs?: RefMap
  readOnly: boolean
  t: TFunction
}

/** A new record on a phone, a step at a time: see `ResourceFormOptions.phoneSteps` */
export interface PhoneSteps {
  steps: { key: string; label: string; sections: (string | string[])[] }[]
  /** Label of the last step, where it is looked over */
  reviewLabel: string
  /** Kept above the buttons on each step (what it comes to so far) */
  // biome-ignore lint/suspicious/noExplicitAny: form values are resource-shaped
  summary?: (form: UseFormReturnType<any>) => ReactNode
  /** The last step: everything as it will be saved; `goTo` a step's key to change it */
  // biome-ignore lint/suspicious/noExplicitAny: form values are resource-shaped
  review: (form: UseFormReturnType<any>, refs: RefMap, goTo: (step: string) => void) => ReactNode
}

export interface ResourceFormOptions {
  /** Inputs for fields marked `form.custom` (e.g. invoice lines, permission matrix) */
  customFields?: Record<string, (props: CustomFieldProps) => ReactNode>
  /**
   * Fields this record's values leave out, their sections with them when nothing is left (a payment voucher's bills
   * on an advance): what is written in them stays, the server judges it
   */
  omit?: (values: Record<string, unknown>) => readonly string[]
  /** Initial values for new records (e.g. the user's home branch); `params` are the page's query string, for links that start one */
  defaults?: (ctx: { me: Me; params: URLSearchParams }) => Record<string, unknown>
  /**
   * Splits the form into tabs. Each tab lists its sections in order; an inner array puts
   * sections side by side on wide screens (sender | receiver). Without it: every section, stacked.
   */
  tabs?: { key: string; label: string; sections: (string | string[])[] }[]
  /**
   * On a phone a new record is filled in a step at a time (each step its sections, checked before going on), then
   * looked over in a last step before it is saved. Elsewhere, and once saved, the tabs as usual.
   */
  phoneSteps?: PhoneSteps
  /** Enter in a one-line input goes to the next input instead of saving (a counter's quick typing) */
  enterMovesOn?: boolean
  /**
   * Asks something before a save (and the submit it leads to): the values to save, changed or as they were, or `false`
   * to stop there. `submitting`: "Submit" or "Save and submit" was pressed
   */
  beforeSave?: (
    values: Record<string, unknown>,
    ctx: { record?: Rec; submitting: boolean; t: TFunction },
  ) => Promise<Record<string, unknown> | false>
  /** Extra buttons next to Save for existing records (print, assign…) */
  actions?: (record: Rec, refs: RefMap) => ReactNode
  /** Content under the form of a saved record, its full width and above its history (a trip's bills on board) */
  below?: (record: Rec, refs: RefMap) => ReactNode
  /** Content shown beside the form on wide screens (timeline, totals…); `form` holds what is being typed */
  // biome-ignore lint/suspicious/noExplicitAny: form values are resource-shaped
  aside?: (record: Rec | undefined, refs: RefMap, form: UseFormReturnType<any>) => ReactNode
  /**
   * Of a saved record, when in one column (phones and tablets) the form's fields come last: the side panel first, then
   * `below`. Where a running trip is and what it carries matter more in hand than its fixed details; a draft is still
   * being filled in, its form first
   */
  detailsLast?: (record: Rec) => boolean
  /**
   * When an existing record can no longer be edited or deleted (an issued invoice): the i18n key
   * of the reason shown above the form. Mirrors the server rule so no one is offered a save it refuses.
   */
  locked?: (record: Rec) => string | false
  /**
   * Whether a submitted record may still be cancelled by the signed-in person, beyond the
   * `cancel` permission (a received transfer: only a lead or accounting). Mirrors the server rule.
   */
  cancellable?: (record: Rec, ability: AppAbility) => boolean
  /**
   * History and comments under an existing record: who made it and changed what (old → new), every print. On for every
   * record, documents and the rest (pay, people, partners, users); `false` only where a record has no story to tell
   */
  activity?: boolean
}

/** Its pages are at its resource's `path`: `shipments` → /shipments, /shipments/new, /shipments/:id */
export interface ResourcePage {
  resource: Resource
  list?: ResourceListOptions
  form?: ResourceFormOptions
  /** Replace the generated list or form page entirely */
  listElement?: ReactNode
  formElement?: ReactNode
}

/** Where a resource's pages are in an app that shows it (`resource.path`) */
export function pagePath(resource: Resource): string {
  if (!resource.path) throw new Error(`${resource.key} has no pages: give it a path (defineResource)`)
  return resource.path
}
